import React, { useEffect, useRef, createRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useInView } from 'react-intersection-observer';

import Image from '~/components/base/Image';

import grayRadial from '~/images/singleIllustrations/grayRadial.png';
import yellowCircle from '~/images/singleIllustrations/yellowCircle.png';
import puddle from '~/images/singleIllustrations/puddle.png';

const illustrationSharedStyles = css`
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    background-repeat: no-repeat;
    background-size: contain;
    z-index: -1;
`;

const ImageGrid = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        display: block;
    }
`;

const ImageCards = styled.div`
    display: flex;
    gap: 20px;
    align-items: flex-end;
    justify-content: space-around;
    width: 100%;
    height: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        display: block;
    }
`;

const ImageCard = styled.div`
    &:nth-of-type(1) {
        position: absolute;
        top: 0;
        left: 0;
        width: 29.8vh;
        max-width: 27vw;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            max-width: 20vw;
        }
    }

    &:nth-of-type(2) {
        position: absolute;
        top: 20%;
        right: 0;
        width: 32.7vh;
        max-width: 28vw;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            max-width: 22vw;
        }

        @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
            top: 18%;
            right: 0;
        }
    }

    &:nth-of-type(3) {
        top: 34%;
        left: 8%;
        width: 28.8vh;
        max-width: 27vw;
        position: absolute;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            max-width: 20vw;
            top: 27%;
        }

        & > div,
        & > img {
            aspect-ratio: 0.73;
            max-width: 20vw;
            width: 22.7vh;
            margin-top: 35px;

            @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
                margin-top: 60px;
                max-width: 15vw;
            }
        }

        &::after {
            ${illustrationSharedStyles};
            max-width: 14vw;
            width: 18.3vh;
            background-image: url(${grayRadial});
        }
    }

    &:nth-of-type(4) {
        position: absolute;
        top: 60%;
        right: 0;
        width: 25.6vh;
        max-width: 24vw;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            top: 45%;
            max-width: 18vw;
        }

        & > div,
        & > img {
            border-radius: 50%;
            max-width: 240px;

            @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
                max-width: 17vw;
                width: 22.8vh;
            }
        }

        &::after {
            ${illustrationSharedStyles};
            top: 50%;
            right: 70%;
            max-width: 40px;
            background-image: url(${yellowCircle});
            z-index: 1;

            @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
                max-width: 80px;
            }

            @media (min-width: ${({ theme }) =>
                    theme.breakpoint.largeDesktop}) {
                max-width: 100px;
            }
        }

        @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
            position: absolute;
            top: 50%;
            right: 8%;
            width: 25.6vh;
            max-width: 18vw;

            &::after {
                right: 0;
                top: 0;
            }
        }
    }

    &:nth-of-type(5) {
        position: absolute;
        top: 75%;
        width: 36.9vh;
        max-width: 32vw;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            max-width: 24vw;
            top: 70%;
        }

        & > div,
        & > img {
            max-width: 370px;
            aspect-ratio: 1.5;

            @media (min-width: ${({ theme }) =>
                    theme.breakpoint.largeDesktop}) {
                max-width: 22vw;
                width: 34.9vh;
            }
        }

        &::after {
            ${illustrationSharedStyles};
            top: 85%;
            max-width: 50px;
            background-image: url(${puddle});

            @media (min-width: ${({ theme }) =>
                    theme.breakpoint.largeDesktop}) {
                max-width: 100%;
            }
        }

        @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
            margin-bottom: 0;
            position: absolute;
            bottom: 0;
            left: 7%;
            width: 36.9vh;
            max-width: 24vw;

            &::after {
                top: -60px;
                max-width: 80px;
            }
        }
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        position: absolute;
    }
`;

const StyledImage = styled(Image)`
    border-radius: 34px;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        border-radius: 59px;
    }
`;

const parallaxCard = (card, index, wrapper) => {
    let modifier = index % 2 === 0 ? 30 : -30;
    if ([1, 3].includes(index)) {
        modifier *= 2;
    }
    let moveAmount = wrapper.getBoundingClientRect().top / wrapper.offsetHeight;
    moveAmount = moveAmount < 0.65 ? moveAmount : 0.65;
    if (card) {
        card.style.transform = `translateY(${moveAmount * modifier}px)`;
    }
};

const ImageCollage = ({ images }) => {
    const { ref, inView } = useInView({
        threshold: 0.1
    });
    const cardsRefs = useRef(images.map(() => createRef()));
    const wrapperRef = useRef();

    useEffect(() => {
        const scrollHandler = () => {
            if (cardsRefs.current?.length) {
                cardsRefs.current.forEach((card, i) => {
                    parallaxCard(card.current, i, wrapperRef.current);
                });
            }
        };
        if (inView) {
            window.addEventListener('scroll', scrollHandler);
        } else {
            window.removeEventListener('scroll', scrollHandler);
        }
        return () => {
            window.removeEventListener('scroll', scrollHandler);
        };
    }, [inView, ref]);

    return (
        <ImageGrid {...{ ref }}>
            <ImageCards ref={wrapperRef}>
                {images.map(({ image }, i) => (
                    <ImageCard key={image.sourceUrl} ref={cardsRefs.current[i]}>
                        <StyledImage {...image} />
                    </ImageCard>
                ))}
            </ImageCards>
        </ImageGrid>
    );
};

ImageCollage.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.shape({}).isRequired
        })
    ).isRequired
};

export default ImageCollage;
